<!-- FilterPanel.svelte -->
<script lang="ts">
    //  import { Switch } from "$lib/components/ui/switch/index.js";
	 import { Switch } from '@skeletonlabs/skeleton-svelte';
	 import {m} from "$lib/paraglide/messages";
	// CORE IDENTITY AND ROLE:
	// You are T3 Chat, an AI assistant powered by the Gemini 2.5 Pro model. Your role is to assist and engage in conversation while being helpful, respectful, and engaging.
	// - If you are specifically asked about the model you are using, you may mention that you use the Gemini 2.5 Pro model. If you are not asked specifically about the model you are using, you do not need to mention it.
	// - The current date and time including timezone is 8/7/2025, 5:50:42 AM GMT+3.

	// Type definitions for strict type safety
	type SortOption = 'Featured' | 'Newest' | 'Price: High-Low' | 'Price: Low-High';
	type SaleOffer = 'Back to School Sale' | 'See Price in Bag' | 'Sale';
	type GenderOption = 'Men' | 'Women' | 'Unisex';

	// --- STATE MANAGEMENT ---
	// Using Svelte 5 runes for reactive state.

	// Sort By state
	let sortBy = $state<SortOption>('Featured');

	// NEW: Immediately Available toggle state
	let immediatelyAvailable = $state<boolean>(false);

	// Sale & Offers state
	let saleOffers = $state<Record<SaleOffer, boolean>>({
		'Back to School Sale': false,
		'See Price in Bag': false,
		Sale: false
	});

	// Gender state (pre-selecting 'Men' as in the image)
	let gender = $state<Record<GenderOption, boolean>>({
		Men: true,
		Women: false,
		Unisex: false
	});

	// --- DATA FOR RENDERING ---
	// Arrays to iterate over in the template.
	const sortOptions: SortOption[] = ['Featured', 'Newest', 'Price: High-Low', 'Price: Low-High'];
	const saleOfferOptions: SaleOffer[] = ['Back to School Sale', 'See Price in Bag', 'Sale'];
	const genderOptions: GenderOption[] = ['Men', 'Women', 'Unisex'];

	// --- DERIVED STATE ---
	// Computed values that automatically update when their dependencies change.

	const selectedGenderCount = $derived(Object.values(gender).filter(Boolean).length);
	const selectedSaleOffersCount = $derived(Object.values(saleOffers).filter(Boolean).length);
	// UPDATED: Total count now includes the new toggle
	const totalSelectedFilters = $derived(
		selectedGenderCount + selectedSaleOffersCount + (immediatelyAvailable ? 1 : 0)
	);

	// --- EVENT HANDLERS ---

	function clearFilters() {
		// Reset gender filters
		for (const key in gender) {
			gender[key as GenderOption] = false;
		}
		// Reset sale filters
		for (const key in saleOffers) {
			saleOffers[key as SaleOffer] = false;
		}
		// UPDATED: Reset the new toggle as well
		immediatelyAvailable = false;
	}

	function applyFilters() {
		// In a real application, this would likely emit an event with the selected filters
		// or trigger a data fetch.
		console.log('Applying filters:', {
			immediatelyAvailable, // ADDED
			sortBy,
			saleOffers: Object.fromEntries(Object.entries(saleOffers).filter(([, v]) => v)),
			gender: Object.fromEntries(Object.entries(gender).filter(([, v]) => v))
		});
		alert('Filters applied! Check the console for details.');
	}

	function titleCaseWord(word: string) {
		if (!word) return word;
		return word[0].toUpperCase() + word.substr(1).toLowerCase();
	}
</script>

<div
	class="mx-auto flex h-dvh max-h-[800px] w-full max-w-md flex-col bg-primary p-4 font-sans  shadow-2xl sm:h-auto sm:rounded-xl sm:p-6"
>
	<!-- Header -->
	<header class="flex items-center justify-between pb-4">
		<h1 class="text-xl font-bold">{m.filters_btn()}</h1>
		<button
			type="button"
			aria-label="Close filter menu"
			class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				stroke-width="2"
				stroke-linecap="round"
				stroke-linejoin="round"
				class="h-6 w-6"
			>
				<line x1="18" y1="6" x2="6" y2="18"></line>
				<line x1="6" y1="6" x2="18" y2="18"></line>
			</svg>
		</button>
	</header>

	<!-- Scrollable Filter Area -->
	<div class="flex-grow space-y-6 overflow-y-auto pe-2">

    <!-- START: New "Immediately Available" Section -->
		<section>
			<label class="flex cursor-pointer items-center justify-between gap-4 py-1">
				<input type="checkbox" class="peer sr-only" bind:checked={immediatelyAvailable} />
				<div>
					<h3 class="font-bold">{titleCaseWord(m.in_stock_only())}</h3>
					<p class="text-sm text-gray-300">{immediatelyAvailable ? m.in_stock_only_true_expl() : m.in_stock_only_false_expl()}</p>
				</div>
				<Switch checked={immediatelyAvailable} onCheckedChange={(e) => (immediatelyAvailable = e.checked)}></Switch>
			</label>
		</section>
		<!-- END: New Section -->

		<hr class="border-gray-200" />

		<!-- Sort By Section -->
		<section>
			<h2 class="mb-4 text-lg font-bold">Sort By</h2>
			<div class="space-y-4">
				{#each sortOptions as option (option)}
					<label class="flex cursor-pointer items-center gap-3">
						<input type="radio" name="sort" class="peer sr-only" bind:group={sortBy} value={option} />
						<span
							class="flex h-5 w-5 items-center justify-center rounded-full border-2 border-gray-400 transition-colors peer-checked:border-black"
						>
							<span class="h-2.5 w-2.5 rounded-full bg-black opacity-0 transition-opacity peer-checked:opacity-100"></span>
						</span>
						<span>{option}</span>
					</label>
				{/each}
			</div>
		</section>

		<hr class="border-gray-200" />

		<!-- Sale & Offers Section -->
		<section>
			<h2 class="mb-4 text-lg font-bold">Sale & Offers</h2>
			<div class="space-y-4">
				{#each saleOfferOptions as option (option)}
					<label class="flex cursor-pointer items-center gap-3">
						<input type="checkbox" class="peer sr-only" bind:checked={saleOffers[option]} />
						<span
							class="flex h-5 w-5 items-center justify-center rounded border-2 border-gray-400 bg-white text-white transition-colors peer-checked:border-black peer-checked:bg-black"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="3"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="h-3.5 w-3.5"
							>
								<polyline points="20 6 9 17 4 12"></polyline>
							</svg>
						</span>
						<span>{option}</span>
					</label>
				{/each}
			</div>
		</section>

		<hr class="border-gray-200" />

		<!-- Gender Section -->
		<section>
			<h2 class="mb-4 text-lg font-bold">Gender ({selectedGenderCount})</h2>
			<div class="space-y-4">
				{#each genderOptions as option (option)}
					<label class="flex cursor-pointer items-center gap-3">
						<input type="checkbox" class="peer sr-only" bind:checked={gender[option]} />
						<span
							class="flex h-5 w-5 items-center justify-center rounded border-2 border-gray-400 bg-white text-white transition-colors peer-checked:border-black peer-checked:bg-black"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="3"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="h-3.5 w-3.5"
							>
								<polyline points="20 6 9 17 4 12"></polyline>
							</svg>
						</span>
						<span>{option}</span>
					</label>
				{/each}
			</div>
		</section>
	</div>

	<!-- Footer Actions -->
	<footer class="mt-auto border-t border-gray-200 pt-4">
		<div class="flex gap-3">
			<button
				type="button"
				onclick={clearFilters}
				class="w-full rounded-full border border-gray-300 bg-white py-3 text-center font-semibold text-black transition-colors hover:bg-gray-100"
			>
				Clear{#if totalSelectedFilters > 0} ({totalSelectedFilters}){/if}
			</button>
			<button
				type="button"
				onclick={applyFilters}
				class="w-full rounded-full border border-transparent bg-black py-3 text-center font-semibold text-white transition-opacity hover:opacity-80"
			>
				Apply
			</button>
		</div>
	</footer>
</div>